

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRedemptionCustomers } from "../../redux/actions/salesmanAction";
import { DashboardLayout } from "../../Pages/dashboard/DashboardLayout";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import {
  FaArrowLeft,
  FaGift,
  FaQrcode,
  FaCalendar,
  FaPhone,
  FaChevronDown,
  FaChevronRight,
  FaMapMarkerAlt,
  FaSearch,
} from "react-icons/fa";
import SearchBar from "../../Pages/Components/SearchBar";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";

const RedemptionCustomersList = () => {
  const dispatch = useDispatch();
  const { loading, redemptionCustomers, error, totalQRCodesUsed } = useSelector(
    (state) => state.salesman
  );
  const { userInfo } = useSelector((state) => state.auth);
  const dashboardData = useSelector((state) => state.dashboard.data);

  const [searchValue, setSearchValue] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const customerLoginType = dashboardData?.customer_login_type || userInfo?.data?.customer_login_type;
  const isPromotional = customerLoginType === "promotional";
  console.log("customerLoginType:", dashboardData?.customer_login_type, userInfo?.customer_login_type, userInfo?.data?.customer_login_type);
  const navigate = useNavigate();

  // Get role_id from dashboard data
  const role_id = dashboardData?.role_id;
  const isRole2 = String(role_id) === "2";

  const BASE_URL = axiosInstance.defaults.baseURL;

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    const separator =
      BASE_URL.endsWith("/") || imagePath.startsWith("/") ? "" : "/";
    return `${BASE_URL}${separator}${imagePath.startsWith("/") ? imagePath.slice(1) : imagePath
      }`;
  };

  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(fetchRedemptionCustomers(userInfo.access_token));
    }
  }, [dispatch, userInfo?.access_token]);

  if (error) {
    const errorMessage = typeof error === 'object' && error !== null
      ? (error.message || error.status || JSON.stringify(error))
      : error;
    return (
      <DashboardLayout>
        <div className="mt-2 max-w-full mx-auto rounded-lg p-2 sm:p-4">
          <p className="text-red-600 font-bold text-center p-4">Error: {errorMessage}</p>
        </div>
      </DashboardLayout>
    );
  }

  const redemptionArray = Array.isArray(redemptionCustomers) ? redemptionCustomers : [];

  // For role 2: show empty state until search; For others: show all by default
  const hasSearchQuery = isRole2 ? searchValue.trim() !== "" : true;

  const filteredRedemptions = redemptionArray
    .filter((redemption) => {
      // If role is 2 and no search, filter out everything
      if (isRole2 && searchValue.trim() === "") {
        return false;
      }

      const search = searchValue.toLowerCase();
      const customerName = redemption.customer?.customer_name?.toLowerCase() || "";
      const customerEmail = redemption.customer?.customer_email?.toLowerCase() || "";
      const customerMobile = redemption.customer?.customer_mobile?.toLowerCase() || "";
      const giftName = redemption.gift?.gift_name?.toLowerCase() || "";
      const dealerName = redemption.customer?.dealer?.name?.toLowerCase() || "";
      const address = redemption.address?.toLowerCase() || "";
      const city = redemption.city?.toLowerCase() || "";

      return (
        customerName.includes(search) ||
        customerEmail.includes(search) ||
        customerMobile.includes(search) ||
        giftName.includes(search) ||
        dealerName.includes(search) ||
        address.includes(search) ||
        city.includes(search)
      );
    })
    .sort((a, b) => {
      return dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf();
    });

  const toggleRowExpansion = (redemptionId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(redemptionId)) {
        newSet.delete(redemptionId);
      } else {
        newSet.add(redemptionId);
      }
      return newSet;
    });
  };

  const totalQRCodes =
    typeof totalQRCodesUsed === "number"
      ? totalQRCodesUsed
      : redemptionArray[0]?.total_qr_codes || 0;

  const MobileRedemptionCard = ({ redemption, isExpanded }) => (
    <div className="bg-white border border-gray-300 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition">
      <div
        className="cursor-pointer"
        onClick={() => toggleRowExpansion(redemption.redemption_id)}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="text-xs text-gray-500 font-medium">Redemption ID</p>
            <p className="font-bold text-blue-600 text-sm">
              #{redemption.redemption_id}
            </p>
          </div>
          <div>
            {isExpanded ? (
              <FaChevronDown className="text-blue-600" />
            ) : (
              <FaChevronRight className="text-gray-600" />
            )}
          </div>
        </div>

        <div className="border-t pt-3 pb-3 space-y-2">
          <div>
            <p className="text-xs text-gray-500 font-medium">Customer</p>
            <p className="font-semibold text-gray-900 text-sm">
              {redemption.customer?.customer_name || "-"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 font-medium">Mobile</p>
              <p className="text-xs text-gray-900 flex items-center gap-1">
                <FaPhone size={10} />
                {redemption.customer?.customer_mobile || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">City</p>
              <p className="text-xs text-gray-900">{redemption.city || "-"}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Email</p>
            <p className="text-xs text-gray-900 break-words">
              {redemption.customer?.customer_email || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Address</p>
            <p className="text-xs text-gray-900 flex items-start gap-1">
              <FaMapMarkerAlt size={10} className="mt-0.5 flex-shrink-0" />
              <span>{redemption.address || "-"}</span>
            </p>
          </div>
        </div>

        <div className="border-t pt-3 pb-3">
          <p className="text-xs text-gray-500 font-medium mb-2">Gift</p>
          <div className="flex gap-2 items-start">
            {redemption.gift?.gift_image && (
              <img
                src={getImageUrl(redemption.gift.gift_image)}
                alt={redemption.gift.gift_name}
                className="w-8 h-8 rounded object-cover flex-shrink-0"
                onError={(e) => {
                  e.target.src =
                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32"%3E%3Crect fill="%23ddd" width="32" height="32"/%3E%3C/svg%3E';
                }}
              />
            )}
            <div className="flex-1">
              <p className="font-semibold text-gray-900 text-sm">
                {redemption.gift?.gift_name || "-"}
              </p>
              <p className="text-xs text-gray-600">
                Scheme: {redemption.gift?.qr_scheme || "-"}
              </p>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                <FaQrcode className="mr-1" size={10} />
                {redemption.total_qr_codes || 0} QR
              </span>
            </div>
          </div>
        </div>

        <div className="border-t pt-3 grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500 font-medium">Date</p>
            <p className="text-xs text-gray-900 flex items-center gap-1">
              <FaCalendar size={10} />
              {dayjs(redemption.created_at).format("DD MMM YYYY")}
            </p>
            <p className="text-xs text-gray-600">
              {dayjs(redemption.created_at).format("HH:mm:ss")}
            </p>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t mt-3 pt-3 space-y-4">
          <div>
            <h4 className="text-xs font-bold text-gray-700 mb-2">
              Customer Verification
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {redemption.customer_sign && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Signature</p>
                  <img
                    src={getImageUrl(redemption.customer_sign)}
                    alt="Customer Signature"
                    className="w-full h-24 rounded object-cover border border-gray-200"
                    onError={(e) => {
                      e.target.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3C/svg%3E';
                    }}
                  />
                </div>
              )}
              {redemption.customer_selfie && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Selfie</p>
                  <img
                    src={getImageUrl(redemption.customer_selfie)}
                    alt="Customer Selfie"
                    className="w-full h-24 rounded object-cover border border-gray-200"
                    onError={(e) => {
                      e.target.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3C/svg%3E';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold text-gray-700 mb-2">
              QR Codes ({redemption.qr_codes?.length || 0})
            </h4>
            {redemption.qr_codes && redemption.qr_codes.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {redemption.qr_codes.map((qr, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 p-2 rounded border border-gray-200 text-xs"
                  >
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <p className="font-mono font-semibold text-gray-900 break-all">
                        {qr.qr_id}
                      </p>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${qr.is_used
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {qr.is_used ? "Used" : "Unused"}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-1">
                      Scheme: {qr.scheme || "-"}
                    </p>
                    <p className="text-gray-600">
                      Created:{" "}
                      {dayjs(qr.created_at).format("DD MMM YYYY HH:mm")}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No QR codes found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="mt-2 max-w-full mx-auto rounded-lg p-2 sm:p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="hover:text-gray-800"
            >
              <FaArrowLeft size={20} />
            </button>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-orange-600">
                Redeemed Gifts
              </h2>
              <span className="text-xs sm:text-sm text-gray-600 font-medium">
                ({filteredRedemptions.length} redemptions)
              </span>
            </div>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-2 sm:gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs sm:text-sm">
                  Total Redeemed
                </p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">
                  {redemptionArray.length}
                </p>
              </div>
              <FaGift className="text-green-300 text-2xl sm:text-3xl" />
            </div>
          </div>
            {!isPromotional && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              
                <div>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    Total QR Codes
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">
                    {totalQRCodes}
                  </p>
                </div>
           
             
              <FaQrcode className="text-blue-300 text-2xl sm:text-3xl" />
            </div>
          </div>
           )}
        </div>
           

        {/* Conditionally show search bar only for role_id 2 */}
        {isRole2 && (
          <div className="flex flex-col gap-3 mb-6">
            <SearchBar
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search customer name / mobile / code ..."
            />
          </div>
        )}

        {loading ? (
          <p className="font-bold text-sm sm:text-md text-center text-gray-950 py-6">
            Loading redemptions...
          </p>
        ) : isRole2 && !hasSearchQuery ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="bg-blue-50 rounded-full p-6 mb-4">
              <FaSearch className="text-blue-400 text-4xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Search to View Redemptions
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-md">
              Enter a customer name, Code, or mobile number
            </p>
          </div>
        ) : filteredRedemptions.length === 0 ? (
          <p className="font-bold text-sm sm:text-md text-center text-gray-950 py-6">
            No redeemed gifts found matching your search
          </p>
        ) : (
          <>
            <div className="lg:hidden">
              {filteredRedemptions.map((redemption) => (
                <MobileRedemptionCard
                  key={redemption.redemption_id}
                  redemption={redemption}
                  isExpanded={expandedRows.has(redemption.redemption_id)}
                />
              ))}
            </div>

            <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow">
              <table className="w-full">
                <thead className="bg-gray-100 border-b sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Address
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Gift
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      {isPromotional ? "Points" : "QR Count"}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Date
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRedemptions.map((redemption, idx) => {
                    const isExpanded = expandedRows.has(redemption.redemption_id);
                    return (
                      <React.Fragment key={idx}>
                        <tr
                          className="border-b hover:bg-blue-50 cursor-pointer transition"
                          onClick={() =>
                            toggleRowExpansion(redemption.redemption_id)
                          }
                        >
                          <td className="px-4 py-3 text-sm">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900">
                                {redemption.customer?.customer_name || "-"}
                              </span>
                              <span className="text-xs text-gray-600">
                                {redemption.customer?.customer_mobile || "-"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900 text-xs">
                                {redemption.city || "-"}
                              </span>
                              <span className="text-xs text-gray-600 truncate">
                                {redemption.address || "-"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              {redemption.gift?.gift_image && (
                                <img
                                  src={getImageUrl(redemption.gift.gift_image)}
                                  alt={redemption.gift.gift_name}
                                  className="w-8 h-8 rounded object-cover"
                                  onError={(e) => {
                                    e.target.src =
                                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32"%3E%3Crect fill="%23ddd" width="32" height="32"/%3E%3C/svg%3E';
                                  }}
                                />
                              )}
                              <span className="font-medium text-gray-900">
                                {redemption.gift?.gift_name || "-"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {isPromotional ? (redemption.gift?.qr_count || 0) : (redemption.total_qr_codes || 0)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-1">
                              <FaCalendar className="text-gray-400" size={12} />
                              {dayjs(redemption.created_at).format(
                                "DD MMM YYYY"
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {dayjs(redemption.created_at).format("HH:mm:ss")}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isExpanded ? (
                              <FaChevronDown className="text-blue-600 mx-auto" />
                            ) : (
                              <FaChevronRight className="text-gray-600 mx-auto" />
                            )}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="bg-gray-50 px-4 py-4">
                              <div className="ml-8 space-y-6">
                                <div>
                                  <h4 className="text-sm font-bold text-gray-700 mb-3">
                                    Customer Verification
                                  </h4>
                                  <div className="grid grid-cols-2 gap-4">
                                    {redemption.customer_sign && (
                                      <div>
                                        <p className="text-xs text-gray-600 mb-2">
                                          Signature
                                        </p>
                                        <img
                                          src={getImageUrl(
                                            redemption.customer_sign
                                          )}
                                          alt="Customer Signature"
                                          className="max-w-xs h-32 rounded border border-gray-200 object-cover"
                                          onError={(e) => {
                                            e.target.src =
                                              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%23ddd" width="200" height="150"/%3E%3C/svg%3E';
                                          }}
                                        />
                                      </div>
                                    )}
                                    {redemption.customer_selfie && (
                                      <div>
                                        <p className="text-xs text-gray-600 mb-2">
                                          Selfie
                                        </p>
                                        <img
                                          src={getImageUrl(
                                            redemption.customer_selfie
                                          )}
                                          alt="Customer Selfie"
                                          className="max-w-xs h-32 rounded border border-gray-200 object-cover"
                                          onError={(e) => {
                                            e.target.src =
                                              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%23ddd" width="200" height="150"/%3E%3C/svg%3E';
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {!isPromotional && (
                                  <div>
                                    <h4 className="text-sm font-bold text-gray-700 mb-3">
                                      QR Codes Used (
                                      {redemption.qr_codes?.length || 0})
                                    </h4>
                                    {redemption.qr_codes &&
                                      redemption.qr_codes.length > 0 ? (
                                      <div className="overflow-x-auto">
                                        <table className="w-full bg-white border border-gray-200 rounded">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              <th className="border border-gray-300 px-4 py-2 text-left text-xs font-semibold text-gray-600">
                                                QR ID
                                              </th>
                                              <th className="border border-gray-300 px-4 py-2 text-left text-xs font-semibold text-gray-600">
                                                Scheme
                                              </th>
                                              <th className="border border-gray-300 px-4 py-2 text-center text-xs font-semibold text-gray-600">
                                                Amount
                                              </th>
                                              <th className="border border-gray-300 px-4 py-2 text-center text-xs font-semibold text-gray-600">
                                                Status
                                              </th>
                                              <th className="border border-gray-300 px-4 py-2 text-left text-xs font-semibold text-gray-600">
                                                Created
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {redemption.qr_codes.map(
                                              (qr, qrIdx) => (
                                                <tr
                                                  key={qrIdx}
                                                  className="border-t hover:bg-gray-50"
                                                >
                                                  <td className="border border-gray-300 px-4 py-2 text-xs font-mono text-gray-900">
                                                    {qr.qr_id}
                                                  </td>
                                                  <td className="border border-gray-300 px-4 py-2 text-xs">
                                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                                      {qr.scheme}
                                                    </span>
                                                  </td>
                                                  <td className="border border-gray-300 px-4 py-2 text-center text-xs text-gray-600">
                                                    {qr.qr_amount || "-"}
                                                  </td>
                                                  <td className="border border-gray-300 px-4 py-2 text-center text-xs">
                                                    <span
                                                      className={`inline-flex px-2 py-1 rounded text-xs font-medium ${qr.is_used
                                                        ? "bg-blue-100 text-blue-800"
                                                        : "bg-gray-100 text-gray-800"
                                                        }`}
                                                    >
                                                      {qr.is_used
                                                        ? "Used"
                                                        : "Unused"}
                                                    </span>
                                                  </td>
                                                  <td className="border border-gray-300 px-4 py-2 text-xs text-gray-600">
                                                    {dayjs(qr.created_at).format(
                                                      "DD MMM YYYY HH:mm"
                                                    )}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-gray-500">
                                        No QR codes found
                                      </p>
                                    )}
                                  </div>
                                )}

                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RedemptionCustomersList;